import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  MessageCircle, Send, FileText, CheckCircle2, Upload, AlertCircle, Loader2,
  Clock, ShieldCheck, Receipt,
} from "lucide-react";
import BackButton from "@/components/business/BackButton";
import PageHeader from "@/components/business/PageHeader";
import SectionHeader from "@/components/business/SectionHeader";
import StatusBanner from "@/components/business/StatusBanner";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import LoadingState from "@/components/business/LoadingState";
import { formatDistanceToNow } from "date-fns";
import { inr } from "@/lib/format";

const STATUS_LABEL = {
  ca_requested: "CA assistance requested",
  ca_in_review: "Under CA review",
  ca_docs_pending: "Awaiting your documents",
  ca_filing: "Being filed by CA at incometax.gov.in",
  filed_with_government: "Filed with the Income Tax Department",
};

const STAGES = [
  { id: "ca_requested", label: "Request received" },
  { id: "ca_in_review", label: "CA review" },
  { id: "ca_docs_pending", label: "Documents" },
  { id: "ca_filing", label: "Filing on Govt portal" },
  { id: "filed_with_government", label: "Filed with ITD" },
];

export default function CAFilingFlow() {
  const { filingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [filing, setFiling] = useState(null);
  const [messages, setMessages] = useState([]);
  const [docReqs, setDocReqs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scroller = useRef(null);

  const load = async () => {
    const [f, m, d, inv, ck] = await Promise.all([
      api.get(`/filings/${filingId}`),
      api.get(`/ca/filings/${filingId}/messages`),
      api.get(`/ca/filings/${filingId}/doc-requests`),
      api.get(`/ca/filings/${filingId}/invoices`),
      api.get(`/ca/filings/${filingId}/checklist`),
    ]);
    setFiling(f.data); setMessages(m.data); setDocReqs(d.data); setInvoices(inv.data); setChecklist(ck.data);
  };

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [filingId]);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/ca/filings/${filingId}/messages`, { text });
      setMessages((m) => [...m, data]);
      setText("");
    } finally { setSending(false); }
  };

  if (!filing) return <AppShell><LoadingState /></AppShell>;

  const stageIdx = STAGES.findIndex(s => s.id === filing.status);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/dashboard" label="Dashboard" />
        <PageHeader>
          <div>
            <PageHeader.Title eyebrow={`${filing.itr_type} · AY ${filing.assessment_year}`}>CA-assisted filing</PageHeader.Title>
            <Badge className="mt-3" variant="outline">{STATUS_LABEL[filing.status] || filing.status}</Badge>
          </div>
        </PageHeader>

        {/* Stage tracker */}
        <Card className="mt-5 p-6 border-border" data-testid="ca-stage-tracker">
          <ol className="flex flex-wrap items-center gap-y-3">
            {STAGES.map((s, i) => {
              const done = i < stageIdx;
              const active = i === stageIdx;
              return (
                <li key={s.id} className="flex items-center">
                  <div className={`grid h-7 w-7 place-items-center rounded-full text-xs font-semibold ${
                    active ? "bg-primary text-primary-foreground" :
                    done ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"
                  }`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </div>
                  <div className={`ml-2 text-xs ${active ? "font-semibold" : "text-muted-foreground"}`}>{s.label}</div>
                  {i < STAGES.length - 1 && <div className={`mx-3 h-px w-8 ${done ? "bg-accent" : "bg-border"}`} />}
                </li>
              );
            })}
          </ol>
          {filing.status === "filed_with_government" && (
            <StatusBanner variant="success" title="Filed with the Income Tax Department" className="mt-5">
              Official ITR-V acknowledgement: <span className="font-mono font-semibold">{filing.itr_v_acknowledgement}</span>
            </StatusBanner>
          )}
        </Card>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Chat */}
          <Card className="border-border flex flex-col h-[70vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-primary" />
              <div className="font-semibold text-sm">Secure chat with your CA</div>
            </div>
            <div ref={scroller} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ca-chat-messages">
              {messages.length === 0 && <div className="text-sm text-muted-foreground text-center pt-8">No messages yet. Say hi to your CA.</div>}
              {messages.map((m) => {
                const mine = m.from_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-lg px-3.5 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      <div className="whitespace-pre-wrap">{m.text}</div>
                      <div className="mt-1 text-[10px] opacity-70">{m.from_role.toUpperCase()} · {formatDistanceToNow(new Date(m.at), { addSuffix: true })}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 flex items-end gap-2">
              <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="min-h-[44px] max-h-32 resize-none" data-testid="ca-chat-input" />
              <Button type="submit" disabled={sending || !text.trim()} data-testid="ca-chat-send">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
            </form>
          </Card>

          <div className="space-y-4">
            {/* Doc requests */}
            <Card className="p-5 border-border" data-testid="doc-requests-card">
              <SectionHeader>Document requests</SectionHeader>
              <div className="mt-3 space-y-2">
                {docReqs.length === 0 && <div className="text-xs text-muted-foreground">No documents requested yet.</div>}
                {docReqs.map(r => (
                  <div key={r.id} className={`rounded-md border p-3 text-xs ${r.status === "pending" ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : "border-border opacity-70"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold">{r.doc_name}</div>
                      <Badge variant="outline" className="text-[10px]">{r.status}</Badge>
                    </div>
                    {r.reason && <div className="mt-1 text-muted-foreground">{r.reason}</div>}
                  </div>
                ))}
              </div>
              <Button asChild size="sm" variant="outline" className="mt-3 w-full"><Link to="/documents"><Upload className="h-3.5 w-3.5 mr-1.5" /> Upload documents</Link></Button>
            </Card>

            {/* Checklist */}
            {checklist && (
              <Card className="p-5 border-border" data-testid="completion-checklist">
                <div className="flex items-center justify-between">
                  <SectionHeader>Completion</SectionHeader>
                  <div className="text-xs font-bold num-rupee">{checklist.progress}%</div>
                </div>
                <ul className="mt-3 space-y-1.5 text-xs">
                  {checklist.items.map(i => (
                    <li key={i.id} className="flex items-center gap-2">
                      <CheckCircle2 className={`h-3.5 w-3.5 ${i.done ? "text-accent" : "text-muted-foreground/40"}`} />
                      <span className={i.done ? "" : "text-muted-foreground"}>{i.label}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Invoices */}
            <Card className="p-5 border-border">
              <SectionHeader><Receipt className="h-3.5 w-3.5" /> Invoices</SectionHeader>
              <div className="mt-3 space-y-2">
                {invoices.length === 0 && <div className="text-xs text-muted-foreground">No invoices yet.</div>}
                {invoices.map(inv => (
                  <div key={inv.id} className="rounded-md border border-border p-3 text-xs flex items-center justify-between">
                    <div>
                      <div className="font-mono">{inv.number}</div>
                      <div className="text-muted-foreground mt-0.5">{inv.description}</div>
                    </div>
                    <div className="num-rupee font-bold">{inr(inv.amount)}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
